import React, { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { Calendar, PieChart as PieChartIcon, BarChart3, TrendingUp, ArrowRight, ArrowUpDown, Building2 } from "lucide-react";
import { useLocation } from "wouter";
import { CATEGORY_LABELS, formatAmount } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { useChartTheme } from "@/hooks/useChartTheme";
import { RouteFeedback } from "@/components/RouteFeedback";
import { PageHeader } from "@/components/PageHeader";
import { Amount } from "@/components/Amount";

const PIE_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#3b82f6", "#8b5cf6", "#ec4899", "#f43f5e", "#14b8a6",
  "#a855f7", "#6366f1",
];

const PIE_COLORS_INCOME = [
  "#22c55e", "#06b6d4", "#3b82f6", "#8b5cf6", "#14b8a6",
  "#a855f7", "#6366f1", "#eab308", "#f97316", "#ec4899",
  "#ef4444", "#f43f5e",
];

function getMonthRange(year: number, month: number) {
  const start = new Date(year, month, 1).getTime();
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999).getTime();
  return { start, end };
}

function getWeekRange() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const start = new Date(now);
  start.setDate(now.getDate() - dayOfWeek);
  start.setHours(0, 0, 0, 0);
  return { start: start.getTime(), end: now.getTime() };
}

/** Format account label for display in dropdown */
function formatAccountLabel(accountNumber: string, bankSource?: string | null, accountLabel?: string | null): string {
  if (accountLabel) return accountLabel;
  if (accountNumber.endsWith('-Main')) {
    const bank = accountNumber.replace('-Main', '');
    return `${bank} Account`;
  }
  if (accountNumber.startsWith('xx')) {
    const digits = accountNumber.replace('xx', '');
    const bank = bankSource?.includes('KFH') ? 'KFH' : (bankSource || '');
    return `${bank} ••${digits}`;
  }
  if (accountNumber === 'KFH-other') {
    return 'KFH Other';
  }
  if (/^\d+$/.test(accountNumber)) {
    const bank = bankSource || '';
    const shortBank = bank.includes('NBK') || bank.includes('National Bank') ? 'NBK'
      : bank.includes('Boubyan') ? 'Boubyan'
      : bank.includes('KFH') ? 'KFH'
      : bank;
    return `${shortBank} ${accountNumber}`;
  }
  return accountNumber;
}

export default function Analytics() {
  const [, setLocation] = useLocation();
  const { t, language, isRTL } = useLanguage();
  const { textColor: chartTextColor, gridColor: chartGridColor, tooltipBg, tooltipBorder, tooltipText, cursorColor, incomeColor, expenseColor, isDark } = useChartTheme();
  const getCategoryLabel = (cat: string) => { const c = cat === "bills_utilities" ? "bills" : cat; return language === "ar" ? (CATEGORY_LABELS_AR[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())) : (CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())); };
  const now = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(() => `${now.getFullYear()}-${now.getMonth()}`);
  const [selectedAccount, setSelectedAccount] = useState<string>("all");
  const [groupBy, setGroupBy] = useState<"category" | "merchant">("category");

  const [year, month] = selectedMonth.split("-").map(Number);
  const { start: startDate, end: endDate } = useMemo(() => getMonthRange(year, month), [year, month]);
  const weekRange = useMemo(() => getWeekRange(), []);

  const accountsQuery = trpc.transaction.accounts.useQuery();
  const { data: accounts } = accountsQuery;
  const accountFilter = selectedAccount === "all" ? undefined : selectedAccount;

  // Comparison month state - defaults to previous month
  const defaultCompareMonth = useMemo(() => {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    return `${prevYear}-${prevMonth}`;
  }, [year, month]);
  const [compareOverride, setCompareOverride] = useState<{ selectedMonth: string; compareMonth: string } | null>(null);
  const compareMonth = compareOverride?.selectedMonth === selectedMonth ? compareOverride.compareMonth : defaultCompareMonth;

  const prevMonthRange = useMemo(() => {
    const [cy, cm] = compareMonth.split("-").map(Number);
    return getMonthRange(cy, cm);
  }, [compareMonth]);

  const categoryDataQuery = trpc.transaction.categoryBreakdown.useQuery({
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: categoryData } = categoryDataQuery;

  const expenseCatsQuery = trpc.transaction.categoryBreakdown.useQuery({
    type: "cash_out",
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: expenseCats } = expenseCatsQuery;

  const incomeCatsQuery = trpc.transaction.categoryBreakdown.useQuery({
    type: "cash_in",
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: incomeCats } = incomeCatsQuery;

  // Previous month category data for comparison
  const prevExpenseCatsQuery = trpc.transaction.categoryBreakdown.useQuery({
    type: "cash_out",
    startDate: prevMonthRange.start,
    endDate: prevMonthRange.end,
    accountNumber: accountFilter,
  });
  const { data: prevExpenseCats } = prevExpenseCatsQuery;

  const prevIncomeCatsQuery = trpc.transaction.categoryBreakdown.useQuery({
    type: "cash_in",
    startDate: prevMonthRange.start,
    endDate: prevMonthRange.end,
    accountNumber: accountFilter,
  });
  const { data: prevIncomeCats } = prevIncomeCatsQuery;

  const merchantDataQuery = trpc.transaction.merchantBreakdown.useQuery({
    startDate,
    endDate,
  });
  const { data: merchantData } = merchantDataQuery;



  const weeklySummaryQuery = trpc.transaction.summary.useQuery({
    startDate: weekRange.start,
    endDate: weekRange.end,
    accountNumber: accountFilter,
  });
  const { data: weeklySummary } = weeklySummaryQuery;

  const monthlySummaryQuery = trpc.transaction.summary.useQuery({
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: monthlySummary } = monthlySummaryQuery;

  // Month options
  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        value: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "long", year: "numeric" }),
      });
    }
    return opts;
  }, [now]);

  // Prepare expense pie chart data
  const expensePieData = useMemo(() => {
    if (!expenseCats) return [];
    return expenseCats.map((cat) => ({
      name: getCategoryLabel(cat.category ?? "uncategorized"),
      rawCategory: cat.category ?? "uncategorized",
      value: parseFloat(cat.total ?? "0"),
      count: cat.count,
    }));
  }, [expenseCats]);

  // Prepare income pie chart data
  const incomePieData = useMemo(() => {
    if (!incomeCats) return [];
    return incomeCats.map((cat) => ({
      name: getCategoryLabel(cat.category ?? "uncategorized"),
      rawCategory: cat.category ?? "uncategorized",
      value: parseFloat(cat.total ?? "0"),
      count: cat.count,
    }));
  }, [incomeCats]);

  // Prepare expense category detail list
  const expenseDetailData = useMemo(() => {
    if (!expenseCats) return [];
    return expenseCats.map((cat) => ({
      category: getCategoryLabel(cat.category ?? "uncategorized"),
      rawCategory: cat.category ?? "uncategorized",
      total: parseFloat(cat.total ?? "0"),
      count: cat.count,
    })).sort((a, b) => b.total - a.total);
  }, [expenseCats]);

  // Prepare income category detail list
  const incomeDetailData = useMemo(() => {
    if (!incomeCats) return [];
    return incomeCats.map((cat) => ({
      category: getCategoryLabel(cat.category ?? "uncategorized"),
      rawCategory: cat.category ?? "uncategorized",
      total: parseFloat(cat.total ?? "0"),
      count: cat.count,
    })).sort((a, b) => b.total - a.total);
  }, [incomeCats]);

  // Monthly comparison data
  const comparisonData = useMemo(() => {
    if (!expenseCats && !prevExpenseCats) return [];
    const currentMap = new Map<string, number>();
    const prevMap = new Map<string, number>();
    expenseCats?.forEach(cat => {
      currentMap.set(cat.category ?? "uncategorized", parseFloat(cat.total ?? "0"));
    });
    prevExpenseCats?.forEach(cat => {
      prevMap.set(cat.category ?? "uncategorized", parseFloat(cat.total ?? "0"));
    });
    const allCategories = new Set([...Array.from(currentMap.keys()), ...Array.from(prevMap.keys())]);
    return Array.from(allCategories).map(cat => ({
      category: getCategoryLabel(cat),
      rawCategory: cat,
      current: currentMap.get(cat) ?? 0,
      previous: prevMap.get(cat) ?? 0,
      change: (currentMap.get(cat) ?? 0) - (prevMap.get(cat) ?? 0),
    })).sort((a, b) => b.current - a.current);
  }, [expenseCats, prevExpenseCats]);

  // Monthly trend data (last 6 months)
  const monthRanges = useMemo(() => {
    const ranges = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(year, month - i, 1);
      const mStart = d.getTime();
      const mEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
      ranges.push({
        label: d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "short" }),
        start: mStart,
        end: mEnd,
      });
    }
    return ranges;
  }, [year, month, language]);

  const m0Query = trpc.transaction.summary.useQuery({ startDate: monthRanges[0]?.start ?? 0, endDate: monthRanges[0]?.end ?? 0 });
  const m1Query = trpc.transaction.summary.useQuery({ startDate: monthRanges[1]?.start ?? 0, endDate: monthRanges[1]?.end ?? 0 });
  const m2Query = trpc.transaction.summary.useQuery({ startDate: monthRanges[2]?.start ?? 0, endDate: monthRanges[2]?.end ?? 0 });
  const m3Query = trpc.transaction.summary.useQuery({ startDate: monthRanges[3]?.start ?? 0, endDate: monthRanges[3]?.end ?? 0 });
  const m4Query = trpc.transaction.summary.useQuery({ startDate: monthRanges[4]?.start ?? 0, endDate: monthRanges[4]?.end ?? 0 });
  const m5Query = trpc.transaction.summary.useQuery({ startDate: monthRanges[5]?.start ?? 0, endDate: monthRanges[5]?.end ?? 0 });
  const { data: m0 } = m0Query;
  const { data: m1 } = m1Query;
  const { data: m2 } = m2Query;
  const { data: m3 } = m3Query;
  const { data: m4 } = m4Query;
  const { data: m5 } = m5Query;

  const monthlyTrendData = useMemo(() => {
    const summaries = [m0, m1, m2, m3, m4, m5];
    return monthRanges.map((r, i) => {
      const s = summaries[i];
      return {
        month: r.label,
        cashIn: parseFloat(s?.find((x) => x.type === "cash_in")?.total ?? "0"),
        cashOut: parseFloat(s?.find((x) => x.type === "cash_out")?.total ?? "0"),
      };
    });
  }, [monthRanges, m0, m1, m2, m3, m4, m5]);

  // Income comparison data
  const incomeComparisonData = useMemo(() => {
    if (!incomeCats && !prevIncomeCats) return [];
    const currentMap = new Map<string, number>();
    const prevMap = new Map<string, number>();
    incomeCats?.forEach(cat => {
      currentMap.set(cat.category ?? "uncategorized", parseFloat(cat.total ?? "0"));
    });
    prevIncomeCats?.forEach(cat => {
      prevMap.set(cat.category ?? "uncategorized", parseFloat(cat.total ?? "0"));
    });
    const allCategories = new Set([...Array.from(currentMap.keys()), ...Array.from(prevMap.keys())]);
    return Array.from(allCategories).map(cat => ({
      category: getCategoryLabel(cat),
      rawCategory: cat,
      current: currentMap.get(cat) ?? 0,
      previous: prevMap.get(cat) ?? 0,
      change: (currentMap.get(cat) ?? 0) - (prevMap.get(cat) ?? 0),
    })).sort((a, b) => b.current - a.current);
  }, [incomeCats, prevIncomeCats]);

  // Prepare bar chart data by category (combined)
  const barData = useMemo(() => {
    if (!categoryData) return [];
    const map = new Map<string, { category: string; cashIn: number; cashOut: number }>();
    for (const item of categoryData) {
      const label = getCategoryLabel(item.category ?? "uncategorized");
      const existing = map.get(label) ?? { category: label, cashIn: 0, cashOut: 0 };
      if (item.type === "cash_in") existing.cashIn = parseFloat(item.total ?? "0");
      else existing.cashOut = parseFloat(item.total ?? "0");
      map.set(label, existing);
    }
    return Array.from(map.values()).sort((a, b) => (b.cashIn + b.cashOut) - (a.cashIn + a.cashOut));
  }, [categoryData]);

  // Prepare merchant grouping data
  const merchantGroupData = useMemo(() => {
    if (!merchantData) return [];
    const map = new Map<string, { merchant: string; total: number; count: number; type: string }>();
    for (const item of merchantData) {
      const key = item.merchant ?? "Unknown";
      const existing = map.get(key);
      if (existing) {
        existing.total += parseFloat(item.total ?? "0");
        existing.count += item.count;
      } else {
        map.set(key, {
          merchant: key,
          total: parseFloat(item.total ?? "0"),
          count: item.count,
          type: item.type,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [merchantData]);


  const monthlyIn = parseFloat(monthlySummary?.find((s) => s.type === "cash_in")?.total ?? "0");
  const monthlyOut = parseFloat(monthlySummary?.find((s) => s.type === "cash_out")?.total ?? "0");

  // Previous month label
  const prevMonthLabel = useMemo(() => {
    const [cy, cm] = compareMonth.split("-").map(Number);
    const d = new Date(cy, cm, 1);
    return d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "short" });
  }, [compareMonth, language]);

  const currentMonthLabel = useMemo(() => {
    const d = new Date(year, month, 1);
    return d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "short" });
  }, [year, month, language]);

  const pageQueries = [accountsQuery, categoryDataQuery, expenseCatsQuery, incomeCatsQuery, prevExpenseCatsQuery, prevIncomeCatsQuery, merchantDataQuery, weeklySummaryQuery, monthlySummaryQuery, m0Query, m1Query, m2Query, m3Query, m4Query, m5Query];
  const isPageLoading = pageQueries.some((query) => query.isLoading);
  const isPageError = pageQueries.some((query) => query.isError);
  const retryPageQueries = () => { pageQueries.forEach((query) => { void query.refetch(); }); };

  if (isPageLoading || isPageError) return <RouteFeedback isLoading={isPageLoading} isError={isPageError} onRetry={retryPageQueries} label={t("analytics.title")} />;

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      <PageHeader title={t("analytics.title")} subtitle={t("analytics.subtitle")} actions={<div className="flex flex-wrap items-center gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="min-h-11 w-[120px] sm:w-[180px] text-sm">
              <Calendar className="me-1.5 h-4 w-4 shrink-0 sm:me-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedAccount} onValueChange={setSelectedAccount}>
            <SelectTrigger className="min-h-11 w-[120px] sm:w-[170px] text-sm">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 hidden sm:block" />
              <SelectValue placeholder={language === "ar" ? "جميع الحسابات" : "All Accounts"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === "ar" ? "جميع الحسابات" : "All Accounts"}</SelectItem>
              {accounts && accounts.filter((acc) => acc.accountNumber != null && acc.isActive !== 0).map((acc) => (
                <SelectItem key={acc.accountNumber!} value={acc.accountNumber!}>
                  {formatAccountLabel(acc.accountNumber!, acc.bankSource, acc.accountLabel)}
                  <span className="ms-1 text-muted-foreground">({acc.count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>} />

      {/* Revenue / Expenses / Net Income / Transactions -- clickable summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div
          className="rounded-xl border-s-4 bg-card p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
          style={{ borderInlineStartColor: "var(--income)" }}
          onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}`)}
        >
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "الداخل" : "Cash In"}</p>
          <p className="text-xs sm:text-xl font-bold mt-1 whitespace-nowrap leading-tight" style={{ color: "var(--income)" }}>
            {monthlyIn > 0 ? <Amount value={monthlyIn} /> : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-0.5">
            {language === "ar" ? "عرض التفاصيل" : "View details"} <ArrowRight className="h-2.5 w-2.5" />
          </p>
        </div>
        <div
          className="rounded-xl border-s-4 bg-card p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
          style={{ borderInlineStartColor: "var(--expense)" }}
          onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}`)}
        >
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "الخارج" : "Cash Out"}</p>
          <p className="text-xs sm:text-xl font-bold mt-1 whitespace-nowrap leading-tight" style={{ color: "var(--expense)" }}>
            {monthlyOut > 0 ? <Amount value={monthlyOut} /> : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-0.5">
            {language === "ar" ? "عرض التفاصيل" : "View details"} <ArrowRight className="h-2.5 w-2.5" />
          </p>
        </div>
        <div
          className="rounded-xl border-s-4 p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
          style={{
            borderInlineStartColor: (monthlyIn - monthlyOut) >= 0 ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)",
            backgroundColor: (monthlyIn - monthlyOut) >= 0 ? "oklch(0.85 0.12 155 / 0.15)" : "oklch(0.75 0.12 25 / 0.15)",
          }}
          onClick={() => setLocation("/income-statement")}
        >
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "صافي التدفق" : "Net Cash Flow"}</p>
          <p className="text-xs sm:text-xl font-bold mt-1 whitespace-nowrap leading-tight" style={{ color: (monthlyIn - monthlyOut) >= 0 ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)" }}>
            {(monthlyIn - monthlyOut) >= 0 ? "+" : ""}{formatAmount(monthlyIn - monthlyOut)}
          </p>
          <p className="text-[10px] mt-1 flex items-center gap-0.5" style={{ color: (monthlyIn - monthlyOut) >= 0 ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)" }}>
            {(monthlyIn - monthlyOut) >= 0 ? (language === "ar" ? "فائض" : "Surplus") : (language === "ar" ? "عجز" : "Deficit")}
          </p>
        </div>
        <div
          className="rounded-xl border-s-4 border-s-primary bg-card p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setLocation(`/transactions?month=${selectedMonth}`)}
        >
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "المعاملات" : "Transactions"}</p>
          <p className="text-xs sm:text-xl font-bold mt-1 text-primary whitespace-nowrap leading-tight">
            {(parseInt(String(monthlySummary?.find((s) => s.type === "cash_in")?.count ?? "0")) + parseInt(String(monthlySummary?.find((s) => s.type === "cash_out")?.count ?? "0"))) || "—"}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-0.5">
            {language === "ar" ? "عرض الكل" : "View all"} <ArrowRight className="h-2.5 w-2.5" />
          </p>
        </div>
      </div>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories" className="gap-2">
            <PieChartIcon className="h-4 w-4" />
            {language === "ar" ? "الفئات" : "Categories"}
          </TabsTrigger>
          <TabsTrigger value="comparison" className="gap-2">
            <ArrowUpDown className="h-4 w-4" />
            {language === "ar" ? "المقارنة" : "Comparison"}
          </TabsTrigger>
          <TabsTrigger value="groups" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            {language === "ar" ? "المجموعات" : "Groups"}
          </TabsTrigger>
        </TabsList>

        {/* ===== CATEGORIES TAB ===== */}
        <TabsContent value="categories" className="space-y-6">
          {/* --- Cash Out Breakdown --- */}
          <div className="space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: "var(--expense)" }} />
              {language === "ar" ? "تفاصيل المصروفات" : "Cash Out Breakdown"}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Expense Pie Chart */}
              <Card className="border-l-4" style={{ borderLeftColor: "var(--expense)" }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "oklch(0.58 0.22 25 / 0.12)" }}>
                      <PieChartIcon className="h-3.5 w-3.5" style={{ color: "var(--expense)" }} />
                    </div>
                    <CardTitle className="text-sm sm:text-base">{language === "ar" ? "توزيع المصروفات" : "Expense Distribution"}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {expensePieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={expensePieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {expensePieData.map((_, index) => (
                            <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: tooltipBg,
                            border: `1px solid ${tooltipBorder}`,
                            borderRadius: "8px",
                            color: tooltipText,
                          }}
                          labelStyle={{ color: tooltipText }}
                          itemStyle={{ color: tooltipText }}
                          formatter={(value: number) => formatAmount(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-10 text-muted-foreground">
                      <PieChartIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{language === "ar" ? "لا توجد مصروفات" : "No expense data"}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Expense Category Detail */}
              <Card className="border-l-4" style={{ borderLeftColor: "var(--expense)" }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "oklch(0.58 0.22 25 / 0.12)" }}>
                      <BarChart3 className="h-3.5 w-3.5" style={{ color: "var(--expense)" }} />
                    </div>
                    <CardTitle className="text-sm sm:text-base">{language === "ar" ? "تفاصيل الفئات" : "Category Detail"}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {expenseDetailData.length > 0 ? (
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {expenseDetailData.map((item, i) => {
                        const maxTotal = expenseDetailData[0]?.total ?? 1;
                        const pct = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;
                        return (
                          <div
                            key={item.rawCategory}
                            className="p-2.5 rounded-lg bg-background/80 border border-border/50 cursor-pointer hover:bg-background/95 hover:border-primary/30 transition-colors"
                            onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}&category=${encodeURIComponent(item.rawCategory)}`)}
                          >
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs sm:text-sm text-foreground font-medium truncate">{item.category}</span>
                              <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                                {formatAmount(item.total)} ({item.count})
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${pct}%`,
                                  backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-muted-foreground">
                      <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{language === "ar" ? "لا توجد بيانات" : "No data available"}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* --- Cash In Breakdown --- */}
          <div className="space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: "var(--income)" }} />
              {language === "ar" ? "تفاصيل الدخل" : "Cash In Breakdown"}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Income Pie Chart */}
              <Card className="border-l-4" style={{ borderLeftColor: "var(--income)" }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "oklch(0.55 0.18 155 / 0.12)" }}>
                      <PieChartIcon className="h-3.5 w-3.5" style={{ color: "var(--income)" }} />
                    </div>
                    <CardTitle className="text-sm sm:text-base">{language === "ar" ? "توزيع الدخل" : "Income Distribution"}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {incomePieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={incomePieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {incomePieData.map((_, index) => (
                            <Cell key={index} fill={PIE_COLORS_INCOME[index % PIE_COLORS_INCOME.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: tooltipBg,
                            border: `1px solid ${tooltipBorder}`,
                            borderRadius: "8px",
                            color: tooltipText,
                          }}
                          labelStyle={{ color: tooltipText }}
                          itemStyle={{ color: tooltipText }}
                          formatter={(value: number) => formatAmount(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-10 text-muted-foreground">
                      <PieChartIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{language === "ar" ? "لا توجد إيرادات" : "No income data"}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Income Category Detail */}
              <Card className="border-l-4" style={{ borderLeftColor: "var(--income)" }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "oklch(0.55 0.18 155 / 0.12)" }}>
                      <BarChart3 className="h-3.5 w-3.5" style={{ color: "var(--income)" }} />
                    </div>
                    <CardTitle className="text-sm sm:text-base">{language === "ar" ? "تفاصيل الفئات" : "Category Detail"}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {incomeDetailData.length > 0 ? (
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {incomeDetailData.map((item, i) => {
                        const maxTotal = incomeDetailData[0]?.total ?? 1;
                        const pct = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;
                        return (
                          <div
                            key={item.rawCategory}
                            className="p-2.5 rounded-lg bg-background/80 border border-border/50 cursor-pointer hover:bg-background/95 hover:border-primary/30 transition-colors"
                            onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}&category=${encodeURIComponent(item.rawCategory)}`)}
                          >
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs sm:text-sm text-foreground font-medium truncate">{item.category}</span>
                              <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                                {formatAmount(item.total)} ({item.count})
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${pct}%`,
                                  backgroundColor: PIE_COLORS_INCOME[i % PIE_COLORS_INCOME.length],
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-muted-foreground">
                      <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{language === "ar" ? "لا توجد بيانات" : "No data available"}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

        </TabsContent>

        {/* ===== COMPARISON TAB ===== */}
        <TabsContent value="comparison">
          {/* Monthly Trend Chart */}
          <Card className="border-l-4 border-l-primary mb-4">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-3.5 w-3.5 text-primary" />
                </div>
                <CardTitle className="text-base">{language === "ar" ? "الاتجاه الشهري" : "Monthly Trend"}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGridColor} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: chartTextColor }}
                      axisLine={{ stroke: chartGridColor }}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11, fill: chartTextColor }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: tooltipBg,
                        border: `1px solid ${tooltipBorder}`,
                        borderRadius: "8px",
                        color: tooltipText,
                      }}
                      labelStyle={{ color: tooltipText, fontWeight: 600, marginBottom: "4px" }}
                      itemStyle={{ color: tooltipText }}
                      formatter={(value: number) => formatAmount(value)}
                      cursor={{ fill: cursorColor }}
                    />
                    <Legend wrapperStyle={{ color: chartTextColor }} />
                    <Bar dataKey="cashIn" name={language === "ar" ? "الدخل" : "Cash In"} fill={incomeColor} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="cashOut" name={language === "ar" ? "المصروفات" : "Cash Out"} fill={expenseColor} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{language === "ar" ? "لا توجد بيانات" : "No trend data available"}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Comparison by Category */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                {language === "ar" ? "مقارنة شهرية" : "Monthly Comparison"}
                <span className="text-xs text-muted-foreground font-normal">
                  ({currentMonthLabel} vs {prevMonthLabel})
                </span>
              </h2>
          <Select value={compareMonth} onValueChange={(nextMonth) => setCompareOverride({ selectedMonth, compareMonth: nextMonth })}>
                <SelectTrigger className="w-[140px] sm:w-[160px] h-8 text-xs border-border bg-background text-foreground">
                  <SelectValue placeholder={language === "ar" ? "مقارنة مع" : "Compare to"} />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.filter(opt => opt.value !== selectedMonth).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Cash Out Comparison */}
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "var(--expense, oklch(0.55 0.18 25))" }} />
              {language === "ar" ? "المصروفات" : "Cash Out"}
            </p>
            <Card className="border-l-4 border-l-red-400/60 mb-4">
              <CardContent className="pt-4">
                {comparisonData.length > 0 ? (
                  <div className="space-y-2">
                    {comparisonData.map((item) => {
                      const changePercent = item.previous > 0
                        ? ((item.change / item.previous) * 100).toFixed(0)
                        : item.current > 0 ? "+100" : "0";
                      const isIncrease = item.change > 0;
                      const isDecrease = item.change < 0;
                      return (
                        <div
                          key={item.rawCategory}
                          className="p-2.5 sm:p-3 rounded-lg border border-border/50 bg-background/80 cursor-pointer hover:bg-background/95 transition-colors"
                          onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}&category=${encodeURIComponent(item.rawCategory)}`)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-foreground truncate">{item.category}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-muted-foreground">
                                  {currentMonthLabel}: {formatAmount(item.current)}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {prevMonthLabel}: {formatAmount(item.previous)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-xs sm:text-sm font-semibold tabular-nums ${isIncrease ? "text-red-400" : isDecrease ? "text-green-400" : "text-muted-foreground"}`}>
                                {isIncrease ? "+" : ""}{formatAmount(item.change)}
                              </p>
                              <p className={`text-[10px] ${isIncrease ? "text-red-400" : isDecrease ? "text-green-400" : "text-muted-foreground"}`}>
                                {isIncrease ? "▲" : isDecrease ? "▼" : "—"} {Math.abs(Number(changePercent))}%
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="text-sm">{language === "ar" ? "لا توجد بيانات مصروفات" : "No expense data"}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cash In Comparison */}
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "var(--income, oklch(0.55 0.18 155))" }} />
              {language === "ar" ? "الدخل" : "Cash In"}
            </p>
            <Card className="border-l-4 border-l-green-400/60">
              <CardContent className="pt-4">
                {incomeComparisonData.length > 0 ? (
                  <div className="space-y-2">
                    {incomeComparisonData.map((item) => {
                      const changePercent = item.previous > 0
                        ? ((item.change / item.previous) * 100).toFixed(0)
                        : item.current > 0 ? "+100" : "0";
                      const isIncrease = item.change > 0;
                      const isDecrease = item.change < 0;
                      return (
                        <div
                          key={item.rawCategory}
                          className="p-2.5 sm:p-3 rounded-lg border border-border/50 bg-background/80 cursor-pointer hover:bg-background/95 transition-colors"
                          onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}&category=${encodeURIComponent(item.rawCategory)}`)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-foreground truncate">{item.category}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-muted-foreground">
                                  {currentMonthLabel}: {formatAmount(item.current)}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {prevMonthLabel}: {formatAmount(item.previous)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-xs sm:text-sm font-semibold tabular-nums ${isIncrease ? "text-green-400" : isDecrease ? "text-red-400" : "text-muted-foreground"}`}>
                                {isIncrease ? "+" : ""}{formatAmount(item.change)}
                              </p>
                              <p className={`text-[10px] ${isIncrease ? "text-green-400" : isDecrease ? "text-red-400" : "text-muted-foreground"}`}>
                                {isIncrease ? "▲" : isDecrease ? "▼" : "—"} {Math.abs(Number(changePercent))}%
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="text-sm">{language === "ar" ? "لا توجد بيانات دخل" : "No income data"}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== GROUPS TAB ===== */}
        <TabsContent value="groups">
          <Card className="border-l-4 border-l-primary">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
                    <BarChart3 className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <CardTitle className="text-base">{language === "ar" ? "مجموعات المعاملات" : "Transaction Groups"}</CardTitle>
                </div>
                <Select value={groupBy} onValueChange={(v) => setGroupBy(v as "category" | "merchant")}>
                  <SelectTrigger className="w-[120px] sm:w-[160px] text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="category">{language === "ar" ? "حسب الفئة" : "By Category"}</SelectItem>
                    <SelectItem value="merchant">{language === "ar" ? "حسب التاجر" : "By Merchant"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {groupBy === "category" ? (
                barData.length > 0 ? (
                  <div className="space-y-2">
                    {barData.map((item) => (
                      <div key={item.category} className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg border border-l-2 border-l-primary gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-foreground truncate">{item.category}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.cashIn > 0 && (
                              <span style={{ color: "var(--income)" }}>In: {formatAmount(item.cashIn)}</span>
                            )}
                            {item.cashIn > 0 && item.cashOut > 0 && " · "}
                            {item.cashOut > 0 && (
                              <span style={{ color: "var(--expense)" }}>Out: {formatAmount(item.cashOut)}</span>
                            )}
                          </p>
                        </div>
                        <p className="text-xs sm:text-sm font-semibold tabular-nums text-foreground whitespace-nowrap">
                          {formatAmount(item.cashIn + item.cashOut)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{language === "ar" ? "لا توجد مجموعات" : "No category groups yet"}</p>
                  </div>
                )
              ) : merchantGroupData.length > 0 ? (
                <div className="space-y-2">
                  {merchantGroupData.map((item) => (
                    <div key={item.merchant} className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg border border-l-2 gap-2" style={{ borderLeftColor: item.type === "cash_in" ? "var(--income)" : "var(--expense)" }}>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-foreground truncate">{item.merchant}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">{item.count} transaction{item.count !== 1 ? "s" : ""}</p>
                      </div>
                      <p
                        className="text-xs sm:text-sm font-semibold tabular-nums whitespace-nowrap"
                        style={{ color: item.type === "cash_in" ? "var(--income)" : "var(--expense)" }}
                      >
                        {formatAmount(item.total)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{language === "ar" ? "لا توجد مجموعات" : "No merchant groups yet"}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
